import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {
  AListItem,
  AListItemAvatar,
  AListItemContent,
  AListItemSubtitle,
  AListItemTitle,
  ATextInput
} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import AssigneeAvatar from "components/AssigneeWidget/AssigneeAvatar";
import {DropdownMenu} from "components/DropdownMenu";
import MultiSelectCheckbox from "components/MultiSelectCheckbox";
import {selectAllUsers, selectCurrentUser} from "store/selectors";
import {FieldNames} from "models/fields";
import css from "./AssigneeMenu.module.scss";

export default function AssigneeMenu({
  assignedUsers = [],
  newAssignees,
  menuPlacement = "bottom",
  setNewAssignees,
  readOnly = false,
  variant = "tertiaryAlt",
  onClose,
  ...rest
}) {
  const {t} = useTranslation();
  const currentUser = useSelector(selectCurrentUser);
  const usersList = useSelector(selectAllUsers);
  const [inputText, setInputText] = useState("");
  const [searchText, setSearchText] = useState("");
  const {userName, userEmail, userId} = currentUser;

  // currentUser fields names are different than usersList field names
  const AssigneeListItem = ({email, name, id}) => (
    <MultiSelectCheckbox
      checkboxAsMenuItem
      data-testid="assigneeCheckbox"
      id={`assignee-${id}`}
      listItemProps={{className: "py-2", "data-testid": "assigneeListItem"}}
      selected={newAssignees}
      setSelected={setNewAssignees}
      stopPropagation
      value={id}>
      <AListItemAvatar className="ma-0 align-self-center">
        <AssigneeAvatar useTooltip={false} {...{email, name}} />
      </AListItemAvatar>
      <AListItemContent className="pa-0 pl-2">
        <AListItemTitle
          data-testid={name ? "assigneeName" : "assigneeEmail"}
          title={name || email}
          style={{fontWeight: 600}}>
          {name || email}
        </AListItemTitle>
        {name && (
          <AListItemSubtitle data-testid="assigneeEmail" title={email}>
            {email}
          </AListItemSubtitle>
        )}
      </AListItemContent>
    </MultiSelectCheckbox>
  );

  // Only filter items after delayed searchText is set
  const filteredItems = useMemo(() => {
    if (searchText.length > 0) {
      const containsValue = (fieldName) =>
        fieldName?.toLowerCase().includes(searchText.toLowerCase());

      return usersList.filter(
        (user) =>
          containsValue(user[FieldNames.userName]) ||
          containsValue(user[FieldNames.userEmail])
      );
    }
  }, [searchText, usersList]);

  // Delay setting searchText to allow user to type more before searching
  useEffect(() => {
    const timer = setTimeout(() => setSearchText(inputText), 300);
    return () => clearTimeout(timer);
  }, [inputText]);

  // Determines which content to show based on condition === true
  const menuContent = [
    // If there's no assignees and no search text, display the current user
    {
      condition: assignedUsers.length === 0 && searchText.length === 0,
      title: "",
      content: (
        <AssigneeListItem email={userEmail} name={userName} id={userId} />
      )
    },
    // If there's assignees and no search text, display the assigned users
    {
      condition: assignedUsers.length > 0 && searchText.length === 0,
      title: t("incidents.assignees.usersAssigned"),
      content: assignedUsers.map((user, i) => (
        <AssigneeListItem
          key={`user-${i}`}
          email={user?.[FieldNames.userEmail]}
          name={user?.[FieldNames.userName]}
          id={user?.[FieldNames.userId]}
        />
      ))
    },
    // If there's search text typed in, display the search results
    {
      condition: searchText.length > 0,
      title: t("incidents.assignees.searchResults", {
        count: filteredItems?.length || 0
      }),
      content: filteredItems?.map((user, i) => (
        <AssigneeListItem
          key={`user-${i}`}
          email={user?.[FieldNames.userEmail]}
          name={user?.[FieldNames.userName]}
          id={user?.[FieldNames.userId]}
        />
      ))
    }
  ];

  const matchingContent = menuContent.find((obj) => obj.condition);

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();
    }
    setSearchText("");
    setInputText("");
  };

  return (
    <DropdownMenu
      buttonClass={classNames("pa-0", css.menuButton)}
      buttonProps={{"data-testid": "assigneeButton", disabled: readOnly}}
      buttonVariant={variant}
      chevron={false}
      closeMenuOnClick={false}
      menuClass={classNames(css.assigneeMenu)}
      menuPlacement={menuPlacement}
      menuProps={{"data-testid": "assigneeMenu"}}
      onClose={() => handleClose()}
      {...rest}>
      <AListItem style={{background: "transparent"}}>
        <ATextInput
          clearable
          medium
          data-testid="assigneeSearch"
          prependIcon="magnifying-glass"
          placeholder={t("incidents.assignees.searchUsers")}
          onChange={(e) => setInputText(e.target.value)}
        />
      </AListItem>
      <AListItem disabled className={css.heading} data-testid="assigneeTitle">
        {matchingContent?.title}
      </AListItem>
      {matchingContent?.content}
    </DropdownMenu>
  );
}

AssigneeMenu.propTypes = {
  /**
   * Array of assignees assigned to the incident
   */
  assignedUsers: PropTypes.array,
  /**
   * Array of new assignees to update the incident with
   */
  newAssignees: PropTypes.array,
  /**
   * Sets the placement of the menu
   *
   * @defaultValue bottom
   */
  menuPlacement: PropTypes.oneOf([
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ]),
  /**
   * If true, clicking the avatars will not open the assignee menu
   *
   * @defaultValue false
   */
  readOnly: PropTypes.bool,
  /**
   * Function to set the new array of assignees
   */
  setNewAssignees: PropTypes.func,
  /**
   * Option to pass in a button variant to the menu button
   *
   * @defaultValue tertiaryAlt
   */
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "medium",
    "tertiary",
    "tertiaryAlt"
  ]),
  /**
   * Function to be called when the menu closes.
   */
  onClose: PropTypes.func
};
